import type { Placement } from '@popperjs/core/lib/enums';
import classNames from 'clsx';
import { get } from 'lodash';
import React, { useRef, useState } from 'react';
import { usePopper } from 'react-popper';

import type { MenuOption } from '@swf/components/Inputs/components/Menu/Menu';
import Menu from '@swf/components/Inputs/components/Menu/Menu';
import useOnClickOutside from '@swf/core/hooks/useOnClickOutside';

import { arrow, container, menu, menuContainer } from './DropdownMenu.scss';
import CaretIcon from './assets/caret.svg';

interface Props {
  content?: React.ReactNode;
  triggerClassName?: string;
  menuClassName?: string;
  placement?: Placement;
  options?: MenuOption[];
  onSelect: (option: MenuOption) => void;
  onClickOutside?: () => void;
}

type TooltipElement = HTMLDivElement | null;

const DropdownMenu: React.FC<Props> = ({
  children,
  content,
  menuClassName,
  triggerClassName,
  placement = 'bottom',
  options,
  onSelect,
  onClickOutside
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [referenceElement, setReferenceElement] = useState<TooltipElement>(null);
  const [popperElement, setPopperElement] = useState<TooltipElement>(null);
  const [arrowElement, setArrowElement] = useState<TooltipElement>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      { name: 'offset', options: { offset: [0, 15] } }
    ]
  });

  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
    !!onClickOutside && onClickOutside();
  });

  const handleSelect = (option: MenuOption) => {
    onSelect(option);

    if (get(option, 'data.skipClose')) return;

    setIsOpen(false);
  };

  return (
    <div className={container} ref={containerRef}>
      <div
        ref={setReferenceElement}
        onClick={() => setIsOpen(!isOpen)}
        className={classNames(triggerClassName)}
      >
        {children}
      </div>
      {isOpen && (
        <div
          className={classNames(menuContainer, menuClassName)}
          ref={setPopperElement}
          style={styles.popper}
          {...attributes.popper}
        >
          {content}
          <Menu className={menu} onSelect={handleSelect} options={options} />
          <div className={arrow} ref={setArrowElement} style={styles.arrow}>
            <CaretIcon />
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
